import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import LabelInputRow from "../../../form/LabelInputRow";
import LabelValueRow from "../../../form/LabelValueRow";
import { getPricingObjectForSpecialRulesAndPricing } from "../../../helpers/pricingUtils";
import { UnsavedChangesProvider } from "../../../../../../contexts/UnsavedChangesContext";
import { PricingGuideText } from "../../../styles";
import { useState } from "react";
import { ModalState } from "../../../../types/types";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import {
  EDIT_BUTTON_TEXT,
  getSpecialPricingByExpenseTypeModalContent,
  VIEW_BUTTON_TEXT,
} from "./helpers";
import { PricingCategory } from "../../../constants";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";

type Props = {
  title: string;
  bodyText?: string;
  formDataPathKey: string;
  disabled?: boolean;
  onClose: () => void;
  tesId?: string;
};

const DRAG_HANDLE_SELECTOR = "drag-handle-base-hours-rules-and-pricing";

const BaseHoursAndSupplementsRulesAndPricingForm = ({
  title,
  bodyText,
  formDataPathKey,
  disabled,
  onClose,
  tesId = "",
}: Props) => {
  const { t } = useTranslation("customers");
  const { getValues: getMainFormValues, setValue: setMainFormValue } =
    useFormContext();

  const mainFormValues = getMainFormValues(formDataPathKey);
  const pricing = mainFormValues?.settings?.pricing;

  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });

  const onOpenModal = (id?: string) => setModalState({ show: true, id });
  const onCloseModal = () =>
    setModalState((prevState) => ({ ...prevState, show: false }));
  const onModalExited = () => {
    setModalState({ show: false, id: "" });
  };

  const defaultValues = {
    baseHours: {
      multiplier: pricing?.baseHours?.multiplier?.value || "",
      expenses: pricing?.baseHours?.expenses,
    },
    supplements: {
      multiplier: pricing?.supplements?.multiplier?.value || "",
      expenses: pricing?.supplements?.expenses,
    },
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const {
    watch,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const onSubmit = (formData: any) => {
    const updatedObject = {
      enabled: mainFormValues.enabled,
      settings: {
        pricing: getPricingObjectForSpecialRulesAndPricing({ formData }),
      },
    };

    setMainFormValue(formDataPathKey, updatedObject, {
      shouldDirty: true,
    });

    onClose();
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => onClose();

  const watchedBaseHours = watch("baseHours");
  const watchedSupplements = watch("supplements");

  const isBaseHoursValid = !!watchedBaseHours.multiplier;
  const isSupplementsValid = !!watchedSupplements.multiplier;

  const showBaseHoursViewButton = !isEmpty(watchedBaseHours.expenses);
  const showSupplementsViewButton = !isEmpty(watchedSupplements.expenses);

  const saveDisabled = !isDirty || !isBaseHoursValid || !isSupplementsValid;

  return (
    <DialogContentWrapper
      title={title}
      margin
      onCancel={onCancel}
      onSave={onSave}
      saveDisabled={saveDisabled}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <PricingGuideText paddingTop="0">{bodyText}</PricingGuideText>
          <LabelValueRow
            label={t("contract.pricingAlt")}
            labelWidth={3}
            value={t("contract.multiplier")}
            valueWidth={9}
          />
          <LabelInputRow
            label={t("settings:BaseHours")}
            labelWidth={3}
            fieldName="baseHours.multiplier"
            fieldType="number"
            inputWidth={3}
            inputSpec={t("contract.multiplier").toLowerCase()}
            inputError={!isBaseHoursValid}
            disabled={disabled}
            decimalScale={3}
            viewButtonText={VIEW_BUTTON_TEXT}
            editButtonText={EDIT_BUTTON_TEXT}
            onOpenModal={() => onOpenModal(PricingCategory.BaseHours)}
            showButton
            showViewButton={showBaseHoursViewButton}
            buttonWidth={5}
          />
          <LabelInputRow
            label={t("settings:Supplements")}
            labelWidth={3}
            fieldName="supplements.multiplier"
            fieldType="number"
            inputWidth={3}
            inputSpec={t("contract.multiplier").toLowerCase()}
            inputError={!isSupplementsValid}
            disabled={disabled}
            decimalScale={3}
            viewButtonText={VIEW_BUTTON_TEXT}
            editButtonText={EDIT_BUTTON_TEXT}
            onOpenModal={() => onOpenModal(PricingCategory.Supplements)}
            showButton
            showViewButton={showSupplementsViewButton}
            buttonWidth={5}
          />
          <DialogComponent
            open={modalState.show}
            onClose={onCloseModal}
            sx={{ display: "flex", maxHeight: "80%" }}
            dragHandleSelector={DRAG_HANDLE_SELECTOR}
            TransitionProps={{ onExited: onModalExited }}
          >
            {getSpecialPricingByExpenseTypeModalContent(
              modalState,
              tesId,
              onCloseModal,
              false,
              true,
              DRAG_HANDLE_SELECTOR,
            )}
          </DialogComponent>
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default BaseHoursAndSupplementsRulesAndPricingForm;
